import { useEffect, useRef, useState } from 'react'
import type { KeyboardEvent, PointerEvent, RefObject } from 'react'

export const AI_CHAT_DEFAULT_WIDTH = 512
export const AI_CHAT_MIN_WIDTH = 320
export const AI_CHAT_KEYBOARD_STEP = 16
const NAVIGATION_WIDTH = 320
const OVERLAY_GUTTER = 48

type Limits = {
  layoutWidth: number | null
  pinned: boolean
  navigationPinned?: boolean
}

export function readStoredWidth(key: string) {
  try {
    const stored = localStorage.getItem(key)
    const width = stored === null ? Number.NaN : Number(stored)

    return Number.isFinite(width) && width > 0 ? width : AI_CHAT_DEFAULT_WIDTH
  } catch {
    return AI_CHAT_DEFAULT_WIDTH
  }
}

export function getMaxWidth({ layoutWidth, pinned, navigationPinned }: Limits) {
  if (!layoutWidth) return null
  if (!pinned) return Math.max(0, layoutWidth - OVERLAY_GUTTER)
  const available = navigationPinned
    ? layoutWidth - NAVIGATION_WIDTH
    : layoutWidth

  return Math.max(0, Math.floor(available / 2))
}

// Available space takes precedence over the minimum width.
export function clampWidth(width: number, max: number | null) {
  const atLeastMin = Math.max(AI_CHAT_MIN_WIDTH, width)

  return max === null ? atLeastMin : Math.min(max, atLeastMin)
}

type Drag = { pointerId: number; startX: number; startWidth: number }

export function useAiChatPanelResize({
  storageKey,
  layoutRef,
  pinned,
  navigationPinned,
}: {
  storageKey: string
  layoutRef: RefObject<HTMLElement | null>
  pinned: boolean
  navigationPinned?: boolean
}) {
  const [preferred, setPreferred] = useState(() => readStoredWidth(storageKey))
  const [draft, setDraft] = useState<number | null>(null)
  const dragRef = useRef<Drag | null>(null)
  const draftRef = useRef<number | null>(null)

  const layoutWidth = useLayoutWidth(layoutRef)
  const max = getMaxWidth({ layoutWidth, pinned, navigationPinned })
  const width = clampWidth(draft ?? preferred, max)

  const persist = (next: number) => {
    setPreferred(next)
    try {
      localStorage.setItem(storageKey, String(next))
    } catch {
      // Storage may be unavailable; the width still applies for this session.
    }
  }

  const finish = () => {
    const next = draftRef.current
    dragRef.current = null
    draftRef.current = null
    setDraft(null)
    if (next !== null) persist(next)
  }

  useNoTextSelection(draft !== null)

  const handleProps = {
    onPointerDown: (event: PointerEvent<HTMLElement>) => {
      if (event.button !== 0) return
      event.preventDefault()
      event.currentTarget.setPointerCapture(event.pointerId)
      dragRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startWidth: width,
      }
    },
    onPointerMove: (event: PointerEvent<HTMLElement>) => {
      const drag = dragRef.current
      if (drag?.pointerId !== event.pointerId) return
      const next = clampWidth(
        drag.startWidth + drag.startX - event.clientX,
        max,
      )
      draftRef.current = next
      setDraft(next)
    },
    onPointerUp: finish,
    onPointerCancel: finish,
    onLostPointerCapture: finish,
    onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
      const delta = getKeyboardDelta(event.key)
      if (delta === 0) return
      event.preventDefault()
      persist(clampWidth(width + delta, max))
    },
  }

  return {
    width,
    min: Math.min(AI_CHAT_MIN_WIDTH, max ?? AI_CHAT_MIN_WIDTH),
    max,
    handleProps,
    finish,
  }
}

function getKeyboardDelta(key: string) {
  if (key === 'ArrowLeft') return AI_CHAT_KEYBOARD_STEP
  if (key === 'ArrowRight') return -AI_CHAT_KEYBOARD_STEP

  return 0
}

function useLayoutWidth(layoutRef: RefObject<HTMLElement | null>) {
  const [layoutWidth, setLayoutWidth] = useState<number | null>(null)
  useEffect(() => {
    const element = layoutRef.current
    if (!element) return
    const measure = () => setLayoutWidth(element.getBoundingClientRect().width)
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(element)

    return () => observer.disconnect()
  }, [layoutRef])

  return layoutWidth
}

function useNoTextSelection(active: boolean) {
  useEffect(() => {
    if (!active) return
    const previous = document.body.style.userSelect
    document.body.style.userSelect = 'none'

    return () => {
      document.body.style.userSelect = previous
    }
  }, [active])
}
